import { useState } from "react";
import { Box, CircularProgress, Tabs, Tab } from "@mui/material";
import { styled, useTheme } from '@mui/material/styles';
import { Ico_Filter_Active_Component, Ico_Filter_Component, Ico_SortAscending, Ico_SortDescending, PageTitle } from '@sagecentral/core-ui';
import { useTaskContext } from "hooks/tasks";
import { CentralDataGridPro, GridBox, GridNoRowsOverlay, GridToolbar } from "components/shared/grids";
import { PreferenceType } from "components/preferences";
import { Sort } from "models/grids";
import { usePreference } from "hooks/preferences";
import { useGridSorting, useGridFilters, useDensityHandler, usePersistedPageSize, useGridMetadata } from "hooks/shared";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import useTaskDependencies from "hooks/tasks/useTaskDependencies";
import useTaskDependenciesColumns from "./useTaskDependenciesColumns";
import { customLocaleText } from "models/grids/HeaderFilterLocaleText";

const NoRowsOverlay = (dependencyType: string) => {
    return (
        <GridNoRowsOverlay text={`No tasks ${dependencyType} found.`} />
    );
};

const TaskFilterTabs = styled(Tabs)(({ theme }) => ({
    color: '#333333',
    '& .MuiTabs-indicator': {
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main,
        height: '1px',
        borderRadius: '4px',
    },
    '&:focus-within': {
        outline: '2px solid #4D90FE',
        outlineOffset: '2px',
    }
}));

const TaskFilterTab = styled(Tab)(({ theme }) => ({
    borderBottom: 1,
    textTransform: 'none',
    '&.Mui-selected': {
        fontWeight: 600,
    },
    '&.Mui-focusVisible': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiTouchRipple-root': {
        display: 'none'
    }
}));

const Dependencies = (): JSX.Element => {
    const taskContext = useTaskContext();
    const { task, template, updateTaskOverview } = taskContext;
    const [dependencyType, setDependencyType] = useState<"predecessor" | "successor">("successor");
    const theme = useTheme();
    const apiRef = useGridApiRef();
    
    const handleTabChange = (_event: React.SyntheticEvent, newValue: "predecessor" | "successor"): void => {
        setDependencyType(newValue);
    };


    const defaultSort: Sort = {
        field: 'endDate',
        dir: 'asc'
    };

    const [preferencesLoaded, settings, setSettings] = usePreference(PreferenceType.TaskDependencies, false);


    const [sort, handleSortChange, sortModel] = useGridSorting({
        defaultSort: [defaultSort],
        settings,
        setSettings
    });

    const [filter, handleFilterChange, filterModel] = useGridFilters({
        settings,
        setSettings
    });

    const [density, handleDensityChange] = useDensityHandler({
        settings,
        setSettings
    });

    const [pagination, handlePaginationModelChange] = usePersistedPageSize({
        settings,
        setSettings
    });

    const gridMetadata = useGridMetadata(filter, sort, settings?.gridSettings, pagination);

    const [result, total, loading, error] = useTaskDependencies(task?.id, dependencyType, gridMetadata, preferencesLoaded);
    const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useTaskDependenciesColumns({
        privilegesLoading: loading,
        settings,
        setSettings
    });

    const isFilterApplied = () => ((gridMetadata.filterParam?.filters?.length || 0) > 0);
    const getData = () => (loading ? [] : result);

    
    return (
        <Box>
            <Box sx={{ display: 'inline-block', mb: '24px' }}>
                <TaskFilterTabs 
                    value={dependencyType} 
                    onChange={handleTabChange}
                    aria-label="Task dependency filter options"
                >
                    <TaskFilterTab
                        label="Before This Task"
                        value="predecessor"
                        id="task-tab-predecessor"
                        aria-label="View tasks that must be completed before this task"
                        aria-controls="task-tabpanel-predecessor"
                        tabIndex={0}
                    />
                    <TaskFilterTab
                        label="After This Task"
                        value="successor"
                        id="task-tab-successor"
                        aria-label="View tasks that depend on this task"
                        aria-controls="task-tabpanel-successor"
                        tabIndex={0}
                    />
                </TaskFilterTabs>
                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mt: '-1px' }} />
            </Box>

            <Box
                id={`task-tabpanel-${dependencyType}`}
                role="tabpanel"
                aria-labelledby={`task-tab-${dependencyType}`}
            >
                <GridBox getData={getData}>
                    <Box sx={{ height: "80vh" }}>
                        <CentralDataGridPro
                            aria-label={`${dependencyType === 'predecessor' ? 'Before' : 'After'} this task grid`}
                            apiRef={apiRef}
                            rows={getData()}
                            rowCount={total}
                            columns={columnsModel}
                            getRowId={(r: any) => r.id}
                            filterMode="server"
                            filterModel={filterModel}
                            sortingMode="server"
                            sortModel={sortModel}
                            paginationMode="server"
                            pagination={true}
                            pageSizeOptions={[25, 50, 75, 100]}
                            paginationModel={pagination}
                            density={'standard'}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: visibilityModel,
                                }
                            }}
                            onSortModelChange={handleSortChange}
                            onFilterModelChange={handleFilterChange}
                            onColumnVisibilityModelChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                            onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                            onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                            onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                            onPaginationModelChange={handlePaginationModelChange}
                            disableDensitySelector={true}
                            slots={{
                                loadingOverlay: () => (<CircularProgress/>),
                                columnMenuSortAscendingIcon: Ico_SortAscending,
                                columnMenuSortDescendingIcon: Ico_SortDescending,
                                columnSortedAscendingIcon: Ico_SortAscending,
                                columnSortedDescendingIcon: Ico_SortDescending,
                                columnUnsortedIcon: undefined,
                                openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                                quickFilterIcon: Ico_Filter_Component,
                                columnFilteredIcon: Ico_Filter_Component,
                                quickFilterClearIcon: Ico_Filter_Component,
                                columnMenuFilterIcon: Ico_Filter_Component,
                                noRowsOverlay: () => NoRowsOverlay(dependencyType),
                                toolbar: GridToolbar
                            }}
                            headerFilters
                            localeText={customLocaleText}
                            loading={loading || !result}
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                            }
                            getRowHeight={() => "auto"}
                        />
                    </Box>
                </GridBox>
            </Box>
        </Box>
    );
};

export default Dependencies;